import { Chip, ChipVariants } from "components/Chip";
import { orderStatusConstants, orderTypeConstants } from "constants/order";
import { distributionLogicValueList } from "constants/paymentPlanConstants";
import { UserRole } from "constants/userRole";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { parseDate } from "helpers/date";
import { centsToDollars, dollarsToCents } from "helpers/price";
import { sizer } from "layout/styles/styled/sizer";
import { getPaymentPlanEnabled } from "pages/CreateOrder/utils/helpers";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { SelectButton } from "primereact/selectbutton";
import { useEffect, useState, useContext, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useUpdateOrderMutation } from "store/queries/order";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import { IOrder } from "types/Order/Order";
import { OrderType } from "types/Order/OrderType";
import { IPayment, IPaymentPlan, IInstallment } from "types/Payment";

import { DownPaymentOnlySection } from "./components/DownPaymentOnlySection";
import { PaymentDetailsSection } from "./components/PaymentDetailsSection";
import { PaymentPlanSection } from "./components/PaymentPlanSection";

export interface IPaymentDetailsEdit {
  paymentPlanMinDownPaymentPercent: number | null;
  paymentPlanDuration: number | null;
  paymentPlanDurationFull: number | null;
  paymentPlanRemainingAmount: number | null;
  paymentPlanMinDownPaymentType?: string;
  paymentPlanMinDownPaymentFlat?: number | null;
  installments?: IInstallment[];
  paymentPlanEnabled: boolean;
  nextInstallmentDate: string;
  providerDistributionLogic?: distributionLogicValueList | null;
  differenceByProvider?: any;
  valuesByProvider?: any;
  nextInstallmentDateChanged?: boolean;
  downpaymentOnlyType?: string;
  downpaymentOnlyPercent?: number | null;
  downpaymentOnlyFlatAmount?: number | null;
  downpaymentOnlyProviderDistributionLogic?: distributionLogicValueList | null;
}

export interface IProviderRemainingAmountRow {
  providerId: string;
  providerName: string;
  cptCode: string;
  providerPriority: number;
  totalExpectedTransferAmount: number;
}

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(2)};
  flex-grow: 1;
  border: 1px solid var(--gray-new-200) !important;
`;

const StyledSelectButton = styled(SelectButton)`
  .p-button {
    margin-right: 8px;
    border-radius: 6px !important;
    padding: 8px 20px;
  }
  .p-button:last-child {
    margin-right: 0;
  }
  .p-button:not(.p-highlight) {
    color: var(--gray-600);
    background-color: var(--gray-50);
    border: 1px solid var(--gray-300);
  }
`;

function PaymentDetails({
  payments,
  paymentPlan,
  orderId,
  onUpdateOrder,
  paymentPlanEditable,
  downpaymentOnlyEditable,
  showPaymentPlan,
  orderStatus,
  account,
  showPaymentPlanEnableButton,
  paymentPlanBreakDown,
  orderType,
  order,
  paymentPlanRemaining,
  showDownpaymentOnly,
}: {
  payments?: IPayment[];
  paymentPlan: IPaymentPlan;
  orderId: string;
  onUpdateOrder: () => void;
  paymentPlanEditable: boolean;
  downpaymentOnlyEditable: boolean;
  showPaymentPlan: boolean;
  showPaymentPlanEnableButton: boolean;
  orderStatus: string;
  account: IAccount;
  paymentPlanBreakDown?: any;
  orderType: OrderType;
  order?: IOrder;
  paymentPlanRemaining?: Array<{
    providerId: string;
    remainingAmountInCents: number;
    providerPriority?: number | null;
  }>;
  showDownpaymentOnly: boolean;
}) {
  const {
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IPaymentDetailsEdit>();
  const [updateOrder] = useUpdateOrderMutation();
  const { current: toastElement } = useContext(ToastContext);
  const { userInfo } = useUserWithAccount();
  const minDownPaymentType = watch("paymentPlanMinDownPaymentType");
  const [editing, setEditing] = useState(false);
  const [paymentPlanFieldsValid, setPaymentPlanFieldsValid] = useState(false);
  const [downpaymentOnlyFieldsValid, setDownpaymentOnlyFieldsValid] =
    useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [saveButtonLoading, setSaveButtonLoading] = useState(false);
  const [
    initialRemainingInstallmentsCount,
    setInitialRemainingInstallmentsCount,
  ] = useState<number | null>(null);
  const [differenceByProvider, setDifferenceByProvider] = useState<{
    [key: string]: number;
  }>({});

  const [valuesByProvider, setValuesByProvider] = useState<{
    [key: string]: number;
  }>({});

  const downPaymentEnabledForAccount = useMemo(() => {
    return account?.partialPaymentsEnabled;
  }, [account?.partialPaymentsEnabled]);

  const paymentPlanEnabledForOrderType = useMemo(() => {
    return getPaymentPlanEnabled(
      orderType as any,
      account?.paymentPlanTypeEnabled
    );
  }, [account?.paymentPlanTypeEnabled, orderType]);

  const initialSelectedPaymentOption = useMemo(() => {
    if (paymentPlan) {
      return paymentPlan.isDownPaymentOnly ? "downPaymentOnly" : "paymentPlan";
    }

    if (paymentPlanEnabledForOrderType && !downPaymentEnabledForAccount) {
      return "paymentPlan";
    }

    if (!paymentPlanEnabledForOrderType && downPaymentEnabledForAccount) {
      return "downPaymentOnly";
    }

    return "paymentPlan";
  }, [
    paymentPlan,
    paymentPlanEnabledForOrderType,
    downPaymentEnabledForAccount,
  ]);

  const [selectedPaymentOption, setSelectedPaymentOption] = useState<string>(
    initialSelectedPaymentOption
  );

  const paymentOptions = useMemo(() => {
    const options = [];
    if (showPaymentPlan && paymentPlanEditable) {
      options.push({ label: "Payment Plan", value: "paymentPlan" });
    }
    if (showDownpaymentOnly && downpaymentOnlyEditable) {
      options.push({ label: "Downpayment only", value: "downPaymentOnly" });
    }
    return options;
  }, [
    showPaymentPlan,
    showDownpaymentOnly,
    paymentPlanEditable,
    downpaymentOnlyEditable,
  ]);

  // Get total patient portion from order data
  const totalPatientPortion = useMemo(() => {
    if (orderType !== orderTypeConstants.GFE) {
      return order?.amountInCents;
    }
    const serviceDetails = order?.serviceDetails;
    if (!serviceDetails) return null;

    // We use updated amount if available
    // We cast to any to access the dynamic properties since TypeScript doesn't know about these fields
    const details = serviceDetails as any;
    return (
      details.updatedPatientResponsibilityEstimateAmountInCents ||
      details.patientResponsibilityEstimateAmountInCents ||
      null
    );
  }, [order?.serviceDetails, orderType, order?.amountInCents]);

  const resetForm = () => {
    setValue(
      "paymentPlanMinDownPaymentPercent",
      paymentPlan?.paymentPlanMinDownPaymentPercent
    );
    setValue(
      "paymentPlanRemainingAmount",
      paymentPlan?.remainingAmountInCents
        ? centsToDollars(paymentPlan?.remainingAmountInCents)
        : null
    );
    setValue(
      "paymentPlanMinDownPaymentFlat",
      paymentPlan?.paymentPlanMinDownPaymentFlat
        ? centsToDollars(paymentPlan?.paymentPlanMinDownPaymentFlat)
        : null
    );
    setValue(
      "paymentPlanMinDownPaymentType",
      paymentPlan?.paymentPlanMinDownPaymentFlat ? "Flat amount" : "Percentage"
    );
    setValue("paymentPlanEnabled", !!paymentPlan);
    setValue(
      "providerDistributionLogic",
      paymentPlan?.providerDistributionLogic ||
        account?.providerDistributionLogic ||
        distributionLogicValueList.BY_PRIORITY
    );
    setValue("nextInstallmentDateChanged", false);

    // Set Value for paymentPlanDuration and nextInstallmentDate
    if (!paymentPlan?.installments?.length) {
      const orderPaid = orderStatus === orderStatusConstants.PAID;
      setValue("nextInstallmentDate", "");
      const duration = paymentPlan?.paymentPlanDuration || 0;
      setValue("paymentPlanDuration", duration);
      setValue("paymentPlanDurationFull", orderPaid ? duration : duration + 1);
      setInitialRemainingInstallmentsCount(duration);
      return;
    }
    // Sort installments Array by dateDue to make sure we get the first installment date
    // Include all installments regardless of date to account for failed installments
    const sortedInstallments = [...paymentPlan.installments].sort(
      (a, b) => new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime()
    ); // Sort by dateDue

    // remaining installments with null paymentId
    const nullPaymentIdCount = sortedInstallments.reduce(
      (count, installment) => {
        return !installment.paymentId ? count + 1 : count;
      },
      0
    );
    // first unpaid installment (by due date)
    const firstNullPaymentIdInstallment = sortedInstallments.find(
      (installment) => !installment.paymentId
    );
    const nextInstallmentDate = firstNullPaymentIdInstallment?.dateDue
      ? parseDate(firstNullPaymentIdInstallment.dateDue, false, true)
      : "-";

    setValue("paymentPlanDuration", nullPaymentIdCount);
    setValue("paymentPlanDurationFull", nullPaymentIdCount);
    setValue("nextInstallmentDate", nextInstallmentDate);
    setInitialRemainingInstallmentsCount(nullPaymentIdCount);
  };

  const resetPartialPaymentForm = () => {
    if (!paymentPlan?.isDownPaymentOnly) {
      setValue("downpaymentOnlyType", "Flat amount");
      setValue("downpaymentOnlyPercent", null);
      setValue("downpaymentOnlyFlatAmount", null);
      return;
    }
    setValue(
      "downpaymentOnlyType",
      paymentPlan?.paymentPlanMinDownPaymentFlat ? "Flat amount" : "Percentage"
    );
    setValue(
      "downpaymentOnlyPercent",
      paymentPlan?.paymentPlanMinDownPaymentPercent || null
    );
    setValue(
      "downpaymentOnlyFlatAmount",
      paymentPlan?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlan?.paymentPlanMinDownPaymentFlat !== undefined
        ? centsToDollars(paymentPlan?.paymentPlanMinDownPaymentFlat)
        : null
    );
  };

  useEffect(() => {
    if (!isFirstRender) {
      return;
    }
    resetForm();
    resetPartialPaymentForm();
    setIsFirstRender(false);
  }, [paymentPlan, setValue, account]);

  useEffect(() => {
    if (isFirstRender) {
      return;
    }
    // Skip resetting flat value when editing a sent order - the value is being
    // calculated dynamically based on number of payments in PaymentPlanSection
    const isSentAndEditing =
      orderStatus === orderStatusConstants.SENT_TO_PATIENT && editing;
    if (minDownPaymentType === "Percentage") {
      setValue("paymentPlanMinDownPaymentFlat", null);
      setValue(
        "paymentPlanMinDownPaymentPercent",
        paymentPlan?.paymentPlanMinDownPaymentPercent || null
      );
    } else if (minDownPaymentType === "Flat amount") {
      setValue("paymentPlanMinDownPaymentPercent", null);
      if (!isSentAndEditing) {
        setValue(
          "paymentPlanMinDownPaymentFlat",
          paymentPlan?.paymentPlanMinDownPaymentFlat
            ? centsToDollars(paymentPlan.paymentPlanMinDownPaymentFlat)
            : null
        );
      }
    }
  }, [minDownPaymentType, orderStatus, editing]);

  const refundedPayment: IPayment | undefined = payments?.find(
    (aPayment) => aPayment.status === "Refunded"
  );

  const mapPaymentDetailsUpdateRequest = (values: IPaymentDetailsEdit) => {
    if (selectedPaymentOption === "downPaymentOnly") {
      const providerDistributionLogicForDownpaymentOnly =
        paymentPlan?.isDownPaymentOnly
          ? paymentPlan?.providerDistributionLogic
          : account?.providerDistributionLogic ||
            distributionLogicValueList.BY_PRIORITY;
      return {
        paymentPlan: [
          {
            isDownPaymentOnly: true,
            active: true,
            differenceByProvider: {},
            hasInstallments: false,
            nextInstallmentDate: null,
            nextInstallmentDateChanged: false,
            paymentPlanDuration: 1,
            paymentPlanMinDownPaymentFlat: values.downpaymentOnlyFlatAmount
              ? dollarsToCents(values.downpaymentOnlyFlatAmount)
              : null,
            paymentPlanMinDownPaymentPercent:
              values.downpaymentOnlyPercent ?? null,
            providerDistributionLogic:
              orderType === orderTypeConstants.PATIENT_RESPONSIBILITY
                ? null
                : providerDistributionLogicForDownpaymentOnly,
            remainingAmountInCents: null,
            valuesByProvider: {},
          },
        ],
      };
    }
    return {
      paymentPlan: [
        {
          remainingAmountInCents:
            values.paymentPlanRemainingAmount !== null &&
            values.paymentPlanRemainingAmount !== undefined
              ? dollarsToCents(values.paymentPlanRemainingAmount)
              : null,
          paymentPlanDuration: values.paymentPlanDuration,
          paymentPlanMinDownPaymentPercent:
            values.paymentPlanMinDownPaymentPercent ?? null,
          active: values.paymentPlanEnabled,
          hasInstallments: !!paymentPlanBreakDown?.payments?.length,
          paymentPlanMinDownPaymentFlat: values.paymentPlanMinDownPaymentFlat
            ? dollarsToCents(values.paymentPlanMinDownPaymentFlat)
            : null,
          nextInstallmentDate: values.nextInstallmentDate || null,
          providerDistributionLogic:
            orderType === orderTypeConstants.PATIENT_RESPONSIBILITY
              ? null
              : values.providerDistributionLogic,
          differenceByProvider: values?.differenceByProvider,
          valuesByProvider: values?.valuesByProvider,
          nextInstallmentDateChanged: values?.nextInstallmentDateChanged,
        },
      ],
    };
  };

  const editButtonDisabled = useMemo(() => {
    if (
      (orderStatus === orderStatusConstants.PARTIALLY_PAID ||
        orderStatus === orderStatusConstants.PAID) &&
      paymentPlanBreakDown?.payments?.length
    ) {
      const iniitalDeposit = paymentPlanBreakDown.payments[0];
      const paymentsWithoutInitialDeposit =
        paymentPlanBreakDown.payments.filter((p: any) => !!p.installmentInDb);
      const lastPaidInstallment = paymentsWithoutInitialDeposit.reduce(
        (last: any, p: any) => (p.installmentInDb?.paymentId ? p : last),
        null
      );
      if (lastPaidInstallment) {
        return !lastPaidInstallment.transferredToProviders;
      }
      return !iniitalDeposit.transferredToProviders;
    }
    return false;
  }, [paymentPlanBreakDown, orderStatus]);

  const submit = () => {
    setSaveButtonLoading(true);
    setValue("differenceByProvider", differenceByProvider);
    setValue("valuesByProvider", valuesByProvider);
    const values = mapPaymentDetailsUpdateRequest(getValues());
    updateOrder({ id: orderId, ...values })
      .unwrap()
      .then(() => {
        setSaveButtonLoading(false);
        onUpdateOrder();
        setEditing(false);
        setDifferenceByProvider({});
        setValuesByProvider({});
        setValue("differenceByProvider", null);
        setValue("valuesByProvider", null);
        toastElement?.show({
          summary: "Success!",
          severity: "success",
          detail: "Your order has been updated.",
        });
      })
      .catch(() => {
        toastElement?.show({
          severity: "error",
          detail: "Try again later.",
          summary: "Something went wrong.",
        });
      });
  };

  const saveButtonDisabled = useMemo(() => {
    if (
      orderStatus === orderStatusConstants.PAID &&
      paymentPlan?.isDownPaymentOnly &&
      selectedPaymentOption === "downPaymentOnly"
    ) {
      return true;
    }
    if (selectedPaymentOption === "paymentPlan") {
      return !paymentPlanFieldsValid;
    }
    return !downpaymentOnlyFieldsValid;
  }, [
    selectedPaymentOption,
    paymentPlanFieldsValid,
    downpaymentOnlyFieldsValid,
  ]);

  const actionFooter = () => {
    return (
      <div className="w-full">
        <Button
          label="Save"
          onClick={submit}
          loading={saveButtonLoading}
          disabled={saveButtonDisabled}
        />
        <Button
          label="Cancel"
          onClick={() => {
            resetForm();
            resetPartialPaymentForm();
            setEditing(false);
            setDifferenceByProvider({});
            setValuesByProvider({});
            setSelectedPaymentOption(initialSelectedPaymentOption);
            setSaveButtonLoading(false);
          }}
          style={{ marginLeft: "0.5em" }}
          className="p-button-text"
        />
      </div>
    );
  };
  const actionHeader = () => {
    return (
      <div className="flex gap-2 align-items-center">
        {editButtonDisabled && (
          <p className="text-primary-400">Transfer is being processed</p>
        )}
        <Button
          icon="pi pi-pencil"
          onClick={() => setEditing(true)}
          disabled={editButtonDisabled}
          className="p-button-outlined-light"
        />
      </div>
    );
  };

  const editButtonVisible = useMemo(() => {
    if (editing || orderStatus === orderStatusConstants.NO_BALANCE_DUE)
      return false;
    if (
      userInfo?.scope !== UserRole.SUPER_ADMIN &&
      orderStatus === orderStatusConstants.PAID &&
      paymentPlan?.isDownPaymentOnly
    ) {
      return false;
    }
    if (paymentPlan?.isDownPaymentOnly && downpaymentOnlyEditable) return true;
    if (paymentPlanEditable) return true;
    return false;
  }, [
    editing,
    downpaymentOnlyEditable,
    paymentPlan?.isDownPaymentOnly,
    paymentPlanEditable,
    orderStatus,
    userInfo?.scope,
    orderStatus,
  ]);

  return (
    <StyledCard
      title="Payment details"
      header={
        refundedPayment && (
          <div className="flex justify-content-end pt-2 pr-3">
            <Chip variant={ChipVariants.WARNING}>Refunded</Chip>
          </div>
        )
      }
    >
      <PaymentDetailsSection payments={payments || []} />
      {(showPaymentPlan || showDownpaymentOnly) && (
        <div className="flex justify-content-between w-full">
          <p className="font-bold text-lg mb-3">Payment Plan</p>
          <div style={{ height: "46px" }}>
            {editButtonVisible && actionHeader()}
          </div>
        </div>
      )}
      {paymentOptions.length > 1 && (
        <StyledSelectButton
          value={selectedPaymentOption}
          options={paymentOptions}
          onChange={(e) => e.value && setSelectedPaymentOption(e.value)}
          className="mb-4"
          disabled={!editing}
        />
      )}
      {showPaymentPlan && selectedPaymentOption === "paymentPlan" && (
        <PaymentPlanSection
          editing={editing}
          editable={paymentPlanEditable}
          orderStatus={orderStatus}
          totalPatientPortion={totalPatientPortion}
          order={order}
          orderType={orderType}
          paymentPlanBreakDown={paymentPlanBreakDown}
          paymentPlan={paymentPlan}
          setPaymentPlanFieldsValid={setPaymentPlanFieldsValid}
          showPaymentPlanEnableButton={showPaymentPlanEnableButton}
          paymentPlanRemaining={paymentPlanRemaining}
          differenceByProvider={differenceByProvider}
          setDifferenceByProvider={setDifferenceByProvider}
          valuesByProvider={valuesByProvider}
          setValuesByProvider={setValuesByProvider}
          initialRemainingInstallmentsCount={initialRemainingInstallmentsCount}
        />
      )}
      {showDownpaymentOnly && selectedPaymentOption === "downPaymentOnly" && (
        <DownPaymentOnlySection
          editing={editing}
          setDownpaymentOnlyFieldsValid={setDownpaymentOnlyFieldsValid}
          order={order as IOrder}
          orderStatus={orderStatus}
        />
      )}
      {editing && actionFooter()}
    </StyledCard>
  );
}

export default PaymentDetails;
